import type { ValueWithUnits, WindowLimitsType, WindowOffsetsType } from '@library/types'
import { useCallback, useEffect, useRef } from 'react'

export const usePinnedResize = (
  isPinnedAtBottomRight: boolean,
  element?: HTMLDivElement,
  anchorElement?: HTMLElement,
  parentElement?: HTMLElement,
  limits?: WindowLimitsType,
  offsets?: WindowOffsetsType,
): void => {
  const mouseOffsetX = useRef<number>(0)
  const mouseOffsetY = useRef<number>(0)
  const elementSize = useRef<DOMRect>()
  const resizeObserver = useRef<ResizeObserver>()

  useEffect(() => {
    if (parentElement && element) {
      resizeObserver.current = new ResizeObserver(() => {
        if (isPinnedAtBottomRight) {
          alignBottomRight()
        } else {
          alignTopRight()
        }
      })
      const observer = resizeObserver.current
      observer.observe(parentElement)

      updateSizes()

      return () => {
        observer.unobserve(parentElement)
      }
    }
  }, [parentElement, element])

  function updateSizes() {
    updateElementSize()
    isPinnedAtBottomRight ? alignBottomRight() : alignTopRight()
  }

  function updateElementSize() {
    if (!element) return
    elementSize.current = element.getBoundingClientRect()
  }

  function alignBottomRight() {
    if (!element || !elementSize.current || !parentElement) return

    const parentSize = parentElement.getBoundingClientRect()
    let { width, height } = elementSize.current
    const {
      left: parentLeft,
      height: parentHeight,
      width: parentWidth,
      top: parentTop,
    } = parentSize

    const { widthMax, heightMax } = getMinMaxes(parentSize, limits)
    if (widthMax && width > widthMax) {
      width = widthMax
    }
    if (heightMax < height) {
      height = heightMax
      element.style.height = `${height - 1}px`
    }

    element.style.left = `${parentLeft + parentWidth - width - (offsets?.x?.value ?? 0)}px`
    element.style.top = `${parentTop + parentHeight - height - (offsets?.y?.value ?? 0)}px`

    updateElementSize()
  }

  function alignTopRight() {
    if (!element || !elementSize.current || !parentElement) return

    const parentSize = parentElement.getBoundingClientRect()
    let { width, height } = elementSize.current
    const { left: parentLeft, height: parentHeight, width: parentWidth } = parentSize

    //учесть лимиты при изменении размера родителя
    const { widthMax, heightMax } = getMinMaxes(parentSize, limits)
    if (widthMax && width > widthMax) {
      width = widthMax
    }
    if (heightMax < height) {
      height = heightMax
      element.style.height = `${height - 1}px`
    }

    element.style.left = `${parentLeft + parentWidth - width - (offsets?.x?.value ?? 0)}px`
    element.style.bottom = `${parentHeight - height - (offsets?.y?.value ?? 0)}px`

    updateElementSize()
  }

  useEffect(() => {
    if (!element || !anchorElement) return

    const anchor = anchorElement

    anchor.addEventListener('mousedown', start)
    window.addEventListener('mouseup', end)

    return () => {
      anchor.removeEventListener('mousedown', start)
      window.removeEventListener('mouseup', end)
    }
  }, [element, anchorElement])

  const start = useCallback(
    ({ clientX, clientY }: MouseEvent) => {
      if (!element) return

      const { left, bottom, top } = element.getBoundingClientRect()
      mouseOffsetX.current = clientX - left

      const parentSize = parentElement?.getBoundingClientRect()

      if (!isPinnedAtBottomRight && parentSize) {
        mouseOffsetY.current = parentSize.top + parentSize.height - clientY + bottom
      } else {
        mouseOffsetY.current = clientY - top
      }

      window.addEventListener('mousemove', move)
    },
    [element],
  )

  const move = useCallback(
    (mouse: MouseEvent) => {
      if (!element || !elementSize.current || !parentElement) return

      const parentSize = parentElement.getBoundingClientRect()
      let left = mouse.pageX - mouseOffsetX.current
      let bottom = mouseOffsetY.current - mouse.pageY
      let top = mouse.pageY - mouseOffsetY.current
      let width = parentSize.left + parentSize.width - left
      let height = !isPinnedAtBottomRight
        ? parentSize.height - bottom
        : parentSize.top + parentSize.height - top

      //учесть лимиты
      const { heightMin, widthMax, heightMax, widthMin } = getMinMaxes(parentSize, limits)
      const xMin = limits?.x?.min?.value ?? 0
      if (width < xMin) {
        width = xMin
        left = parentSize.left + parentSize.width - width
      }
      if (widthMax && width > widthMax) {
        width = widthMax
        left = parentSize.left + parentSize.width - width
      }
      if (heightMax < height) {
        height = heightMax
        bottom = parentSize.height - height
        top = parentSize.top + parentSize.height - height
      }
      const offsetY = offsets?.y?.value ?? 0
      if (heightMin + offsetY > height) {
        height = heightMin + offsetY
        bottom = parentSize.height - height
        top = parentSize.top + parentSize.height - height
      }

      const offsetX = offsets?.x?.value ?? 0
      if (widthMin + offsetX > width) {
        width = widthMin + offsetX
        left = parentSize.left + parentSize.width - width
      }

      element.style.left = `${left}px`
      if (!isPinnedAtBottomRight) {
        element.style.bottom = `${bottom}px`
      } else {
        element.style.top = `${top}px`
      }
      element.style.width = `${width - 1 - offsetX}px`
      element.style.height = `${height - offsetY - 1}px`
      element.style.overflow = 'hidden'
    },
    [element, limits?.x?.max, limits?.x?.min, limits?.y?.max, parentElement],
  )

  const end = useCallback(() => {
    if (!element) return

    updateElementSize()

    window.removeEventListener('mousemove', move)
  }, [element])
}

const getMinMaxes = (
  parentSize: DOMRect,
  limits?: WindowLimitsType,
): { widthMin: number; widthMax: number; heightMin: number; heightMax: number } => {
  const getValue = (parentSize: number, limit: ValueWithUnits | undefined) => {
    switch (limit?.units) {
      case 'value': {
        return limit.value
      }
      case 'percent': {
        return parentSize * limit.value
      }
      default: {
        return parentSize
      }
    }
  }
  const widthMax = getValue(parentSize.width, limits?.x?.max)
  const heightMax = getValue(parentSize.height, limits?.y?.max)
  const widthMin = getValue(parentSize.width, limits?.x?.min)
  const heightMin = getValue(parentSize.height, limits?.y?.min)

  return { widthMin, widthMax, heightMin, heightMax }
}
